import type {
  AbandonReason,
  ActivityEnd,
  ActivityStart,
  ActivityType,
  EventIn,
  EventType,
  Modality,
  StudySessionStart,
} from '@nelnela/contracts';
import { api, ApiError, clientKind, type ClientKind } from './api';
import type { EventQueue } from './events';
import { ulid } from './ulid';

/**
 * Framework-free study-session / activity lifecycle (04-flows "Session & activity lifecycle"):
 * app open → sessionStart; entering an activity screen → activityStart; leaving → activityEnd.
 * Ids are client-generated so every call is a safe retry. The client never emits `lookup`
 * (gloss writes it server-side) nor `activity_started/completed` (activityStart/activityEnd
 * write them) — only behavior events via createEmitter.
 */

export { clientKind };
export type { ClientKind };

export interface StudySessionHandle {
  id: string;
  profileId: string;
  client: ClientKind;
  /** Resolves once sessionStart landed on the server (the event queue's gate). Never rejects. */
  ready: Promise<void>;
}

const MAX_RETRY_MS = 30_000;
const DEFAULT_RETRY_MS = 1000;
const DEFAULT_ACTIVITY_TRIES = 10;

const defaultSleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/** crypto.randomUUID needs a secure context; LAN-IP dev on a phone (http://192.168.…) has none. */
export function uuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
const backoff = (base: number, attempt: number): number =>
  Math.min(base * 2 ** (attempt - 1), MAX_RETRY_MS);

function appVersion(): string {
  // vite/client types are not in this tsconfig; import.meta.env exists under Vite and Vitest.
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  return env?.VITE_APP_VERSION ?? 'dev';
}

/** Retryable = the request may succeed later: transport failure, 409 (FK not landed), 429, 5xx. */
function isTransient(err: unknown): boolean {
  if (!(err instanceof ApiError)) return true;
  const s = err.problem.status;
  return s === 409 || s === 429 || s >= 500;
}

// One StudySession per page load: module state, keyed by profileId.
let current: StudySessionHandle | null = null;

export function currentStudySession(): StudySessionHandle | null {
  return current;
}

export interface StudySessionDeps {
  start?: typeof api.sessionStart;
  now?: () => Date;
  retryMs?: number;
  sleep?: (ms: number) => Promise<void>;
}

/**
 * Idempotent per page load: the same profileId returns the existing handle; a different one
 * replaces it (the caller ends the old session if it wants to). The POST retries forever with
 * backoff — the app keeps working meanwhile; events queue behind `ready`.
 */
export function startStudySession(
  profileId: string,
  deps: StudySessionDeps = {},
): StudySessionHandle {
  if (current && current.profileId === profileId) return current;
  const start = deps.start ?? api.sessionStart;
  const now = deps.now ?? (() => new Date());
  const retryMs = deps.retryMs ?? DEFAULT_RETRY_MS;
  const sleep = deps.sleep ?? defaultSleep;

  const id = uuid();
  const client = clientKind();
  const body: StudySessionStart = {
    id,
    profileId,
    client,
    appVersion: appVersion(),
    startedAt: now().toISOString(),
  };
  const ready = (async () => {
    for (let attempt = 1; ; attempt++) {
      try {
        await start(body);
        return;
      } catch (err) {
        if (!isTransient(err)) {
          // A permanent 4xx (a client bug — e.g. unknown profile) will never succeed; retrying
          // forever would leave the event gate closed and the queue growing. Open the gate: the
          // server 202-defers those events and the queue gives them up after MAX_DEFERRALS.
          console.error(
            '[session] sessionStart refused; events for this session will not land',
            err,
          );
          return;
        }
        console.warn(`[session] sessionStart failed (attempt ${attempt})`, err);
        await sleep(backoff(retryMs, attempt));
      }
    }
  })();
  current = { id, profileId, client, ready };
  return current;
}

/** Best effort: the server also closes sessions it never hears the end of. */
export async function endStudySession(
  h: StudySessionHandle,
  deps: { end?: typeof api.sessionEnd; now?: () => Date; keepalive?: boolean } = {},
): Promise<void> {
  const end = deps.end ?? api.sessionEnd;
  const now = deps.now ?? (() => new Date());
  try {
    await end(h.id, { endedAt: now().toISOString() }, { keepalive: deps.keepalive ?? false });
  } catch (err) {
    console.warn('[session] sessionEnd failed', err);
  }
  if (current === h) current = null;
}

export interface ActivityHandle {
  id: string;
  type: ActivityType;
  /** Idempotent: the first call POSTs activityEnd, later calls are no-ops. Never throws. */
  end(opts?: {
    abandoned?: boolean;
    abandonmentReason?: AbandonReason;
    touchedItems?: string[];
    /** M6a: PLAYING time in ms, for a watch or listen activity. Omitted = nothing measured. */
    playedMs?: number;
    /** pagehide path: let the request outlive the page */
    keepalive?: boolean;
  }): Promise<void>;
}

export interface ActivityDeps {
  start?: typeof api.activityStart;
  end?: typeof api.activityEnd;
  now?: () => Date;
  retryMs?: number;
  maxTries?: number;
  sleep?: (ms: number) => Promise<void>;
  uuid?: () => string;
  /** M6a: the bite tile this activity was launched from. Both or neither — `planIdx` without a
   *  `dailyBiteId` is an index into nothing, and the server drops it. */
  dailyBiteId?: string;
  planIdx?: number;
}

/**
 * Waits for the session to land, then POSTs activityStart with a fresh uuid, retrying transient
 * failures (409 = session row not visible yet, network, 429, 5xx) with backoff up to maxTries;
 * gives up by throwing so the screen can decide (M2: log and keep watching, events carry
 * activityId null). The same id is re-used across retries: a duplicate POST must be idempotent.
 */
export async function startActivity(
  session: StudySessionHandle,
  type: ActivityType,
  target?: Record<string, string | number>,
  deps: ActivityDeps = {},
): Promise<ActivityHandle> {
  const start = deps.start ?? api.activityStart;
  const end = deps.end ?? api.activityEnd;
  const now = deps.now ?? (() => new Date());
  const retryMs = deps.retryMs ?? DEFAULT_RETRY_MS;
  const maxTries = deps.maxTries ?? DEFAULT_ACTIVITY_TRIES;
  const sleep = deps.sleep ?? defaultSleep;
  const newId = deps.uuid ?? uuid;

  await session.ready;
  const id = newId();
  const body: ActivityStart = {
    id,
    sessionId: session.id,
    profileId: session.profileId,
    type,
    ...(target ? { target } : {}),
    // The bite's grip on the activity. Sent together or not at all: the end handler uses the
    // pair to find the tile to mark complete, and a planIdx with no bite would point at nothing.
    ...(deps.dailyBiteId !== undefined && deps.planIdx !== undefined
      ? { dailyBiteId: deps.dailyBiteId, planIdx: deps.planIdx }
      : {}),
    startedAt: now().toISOString(),
  };
  for (let attempt = 1; ; attempt++) {
    try {
      await start(body);
      break;
    } catch (err) {
      if (!isTransient(err) || attempt >= maxTries) throw err;
      await sleep(backoff(retryMs, attempt));
    }
  }

  let ended = false;
  return {
    id,
    type,
    async end(opts) {
      if (ended) return;
      ended = true;
      const abandoned = opts?.abandoned ?? false;
      const payload: ActivityEnd = {
        endedAt: now().toISOString(),
        abandoned,
        // CHECK: abandonment_reason only if abandoned.
        ...(abandoned && opts?.abandonmentReason
          ? { abandonmentReason: opts.abandonmentReason }
          : {}),
        ...(opts?.touchedItems?.length ? { touchedItems: opts.touchedItems } : {}),
        ...(opts?.playedMs !== undefined
          ? { playedMs: Math.max(0, Math.round(opts.playedMs)) }
          : {}),
      };
      try {
        await end(id, payload, { keepalive: opts?.keepalive ?? false });
      } catch (err) {
        console.warn(`[session] activityEnd failed for ${id}`, err);
      }
    },
  };
}

export interface EmitInput {
  eventType: EventType;
  activityId: string | null;
  activityType: ActivityType;
  itemId?: string | null;
  surface?: string;
  modality?: Modality;
  correct?: boolean;
  latencyMs?: number;
  support?: EventIn['support'];
  context?: EventIn['context'];
  tsClient?: string;
}

/** Stamps identity + time on a behavior observation. Keys left undefined are omitted. */
export function makeEvent(session: StudySessionHandle, input: EmitInput): EventIn {
  const e: EventIn = {
    id: ulid(),
    profileId: session.profileId,
    sessionId: session.id,
    activityId: input.activityId,
    activityType: input.activityType,
    eventType: input.eventType,
    tsClient: input.tsClient ?? new Date().toISOString(),
    client: session.client,
    schemaVersion: 1,
  };
  if (input.itemId !== undefined) e.itemId = input.itemId;
  if (input.surface !== undefined) e.surface = input.surface;
  if (input.modality !== undefined) e.modality = input.modality;
  if (input.correct !== undefined) e.correct = input.correct;
  if (input.latencyMs !== undefined) e.latencyMs = input.latencyMs;
  if (input.support !== undefined) e.support = input.support;
  if (input.context !== undefined) e.context = input.context;
  return e;
}

/** makeEvent + enqueue; returns the event (its id is the idempotency key). */
export function createEmitter(
  session: StudySessionHandle,
  queue: EventQueue,
): (input: EmitInput) => EventIn {
  return (input) => {
    const e = makeEvent(session, input);
    queue.enqueue(e);
    return e;
  };
}
